import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { RoutesApi, TripRouteScheduleResponse } from '@/shared/api/routes'

interface UseTripRouteScheduleOptions {
  skip?: boolean
}

export function useTripRouteSchedule(tripId?: string, options?: UseTripRouteScheduleOptions) {
  const { getToken } = useAuth()
  const [schedule, setSchedule] = useState<TripRouteScheduleResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const routesApi = useCallback(() => new RoutesApi(getToken), [getToken])

  const fetchSchedule = useCallback(
    async (targetTripId?: string) => {
      const effectiveTripId = targetTripId ?? tripId
      if (!effectiveTripId) {
        return null
      }

      try {
        setLoading(true)
        setError(null)
        const api = routesApi()
        const data = await api.getTripRouteSchedule(effectiveTripId)
        setSchedule(data)
        return data
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch route schedule'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [routesApi, tripId]
  )

  useEffect(() => {
    if (!options?.skip && tripId) {
      fetchSchedule(tripId).catch(() => {
        // Errors already captured in state
      })
    }
  }, [fetchSchedule, options?.skip, tripId])

  /**
   * Optimistically update a waypoint in the route schedule
   * This allows immediate UI updates without waiting for API calls
   */
  const updateWaypointInSchedule = useCallback((waypointId: string, updates: { start_time?: string | null; end_time?: string | null }) => {
    setSchedule(prev => {
      if (!prev) return prev
      
      const updated = { ...prev }
      let found = false
      
      // Update waypoint in all days and routes
      updated.days = prev.days.map(day => ({
        ...day,
        routes: day.routes.map(route => ({
          ...route,
          waypoints: route.waypoints?.map(wp => {
            if (wp.id === waypointId) {
              found = true
              return {
                ...wp,
                start_time: updates.start_time !== undefined 
                  ? (updates.start_time === null ? undefined : updates.start_time)
                  : wp.start_time,
                end_time: updates.end_time !== undefined 
                  ? (updates.end_time === null ? undefined : updates.end_time)
                  : wp.end_time,
              }
            }
            return wp
          })
        }))
      }))
      
      if (!found) {
      }
      
      return updated
    })
  }, [])

  return {
    schedule,
    loading,
    error,
    refresh: fetchSchedule,
    updateWaypointInSchedule
  }
}

